// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
// Components
import DetailsGrid from '@/common-utils/components/renders/detailsGrid/DetailsGrid';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import ModalUpdateSubDomain from '../modals/details/ModalUpdateSubDomain';
import ModalUpdateOrganizationBrand from '../modals/details/ModalUpdateOrganizationBrand';
// Common
import ICONS from '@/common-utils/enums/icons';
import organizationFields from '../../fields/organizationFields';
import Permissions from '@/app/permissions/Permissions';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';

/**
 * @param root0
 * @param root0.brand
 * @param root0.organization
 * @param root0.setLaunchRefetch
 * @returns {JSx} CustomizationPanel
 */
function CustomizationPanel({ brand, organization, setLaunchRefetch }) {
    // State
    const [isSubDomainModalOpen, setIsSubDomainModalOpen] = useState(false);
    const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);

    // Store
    const permissions = useSessionStore(s => s.session.permissions);

    // Constants
    const { domainName, style } = brand;
    const { mainColor, assets } = style;
    const { logoPath } = assets;
    const data = {
        [generateNestedFieldAccessor(organizationFields.BRAND.DOMAIN_NAME)]: domainName,
        [generateNestedFieldAccessor(organizationFields.BRAND.STYLE.MAIN_COLOR)]: mainColor,
        [generateNestedFieldAccessor(organizationFields.BRAND.STYLE.ASSETS.LOGO_PATH)]: logoPath || ''
    };

    const customizationDataToDisplay = {
        domainName: organizationFields.BRAND.DOMAIN_NAME,
        mainColor: organizationFields.BRAND.STYLE.MAIN_COLOR,
        logoPath: organizationFields.BRAND.STYLE.ASSETS.LOGO_PATH
    };

    return (
        <>
            <PageActions
                actions={
                    [
                        {
                            icon: ICONS.DOMAIN,
                            entity: 'actions.page.update_subdomain',
                            namespace: nsOrganization,
                            onClick: () => setIsSubDomainModalOpen(true),
                            visible: true,
                            disabled: !hasRequiredPermissions({
                                permissions: permissions,
                                requiredPermissions: [Permissions.MB_ADMIN.name]
                            }),
                            tooltipForbidden: 'title.permissions.access_denied'
                        },
                        {
                            icon: ICONS.CUSTOMIZATION,
                            entity: 'actions.page.update_brand',
                            namespace: nsOrganization,
                            onClick: () => setIsBrandModalOpen(true),
                            visible: true,
                            disabled: !hasRequiredPermissions({
                                permissions: permissions,
                                requiredPermissions: [Permissions.MB_PERSONALIZE.name]
                            }),
                            tooltipForbidden: 'title.permissions.access_denied'
                        }
                    ]
                }
                retractable
            />
            <DetailsGrid
                data={data}
                fields={customizationDataToDisplay}
                permissions={permissions}
            />
            <ModalUpdateSubDomain
                isOpen={isSubDomainModalOpen}
                setIsOpen={setIsSubDomainModalOpen}
                organizationId={organization.uuid}
                urlSubDomain={domainName}
                setLaunchRefetch={setLaunchRefetch}
                modalTitle='modal.update_subdomain.title'
            />
            <ModalUpdateOrganizationBrand
                isOpen={isBrandModalOpen}
                setIsOpen={setIsBrandModalOpen}
                organization={organization}
                setLaunchRefetch={setLaunchRefetch}
            />
        </>
    );
}

CustomizationPanel.propTypes = {
    brand: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        domainName: PropTypes.string,
        style: PropTypes.shape({
            mainColor: PropTypes.string,
            assets: PropTypes.shape({
                logoPath: PropTypes.string
            })
        })
    }).isRequired,
    organization: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default CustomizationPanel;